import isEqual from 'lodash/isEqual';
import type { ColorMap } from '../../core/asset/ColorMap';
import { Config } from '../../video-data/config';
import { cloneDeep } from '../cloneDeep';
import type { BasePlugin } from './plugins/BasePlugin';
import { ColorsPlugin } from './plugins/ColorsPlugin';
import { TransitionsPlugin } from './plugins/TransitionsPlugin';
import type { Lottie } from './types/lottie';
import { assertIsValidLottie } from './validators';

const COLORS_PLUGIN = 'COLORS_PLUGIN';
const TRANSITIONS_PLUGIN = 'TRANSITIONS_PLUGIN';

type ChangeFn = (state: Lottie) => void;

type Plugins = {
    [COLORS_PLUGIN]: ColorsPlugin | null;
    [TRANSITIONS_PLUGIN]: TransitionsPlugin | null;
};

export class AnimationData {
    #originalData: Lottie;
    #currentData: Lottie;

    #plugins: Plugins = {
        [COLORS_PLUGIN]: null,
        [TRANSITIONS_PLUGIN]: null,
    };

    constructor(json: Lottie) {
        assertIsValidLottie(json);
        this.#originalData = cloneDeep(json);
        this.#currentData = cloneDeep(this.#originalData);
    }

    get originalValue(): Lottie {
        return this.#originalData;
    }

    get value(): Lottie {
        return this.#currentData;
    }

    get width() {
        return this.#originalData.w;
    }

    get height() {
        return this.#originalData.h;
    }

    get originalFrameRate() {
        return this.#originalData.fr;
    }

    get frameRate() {
        return this.#currentData.fr;
    }

    get totalFrames() {
        return this.#originalData.op - this.#originalData.ip;
    }

    get playbackSpeed() {
        if (this.#currentData.op === 0) {
            return 0;
        }
        return this.frameRate / this.originalFrameRate;
    }

    get originalDuration() {
        const durationInSeconds = this.totalFrames / this.originalFrameRate;
        const durationInMilliseconds = durationInSeconds * 1000;
        return durationInMilliseconds;
    }

    get duration() {
        if (this.#currentData.op === 0) {
            return 0;
        }
        const durationInSeconds = this.totalFrames / this.frameRate;
        const durationInMilliseconds = durationInSeconds * 1000;
        return durationInMilliseconds;
    }

    get firstFrameIndex(): number {
        // First frame of Lottie animation.
        // Note: it is always 0 because lottie-web library internally handles non-zero first frames.
        return 0;
    }

    get lastFrameIndex(): number {
        return this.totalFrames - 1;
    }

    get colorMaps() {
        return this.colorsPlugin.colorMaps;
    }

    get colorsPlugin() {
        if (!this.#plugins[COLORS_PLUGIN]) {
            this.useColorsPlugin();
        }
        return this.#plugins[COLORS_PLUGIN];
    }

    /**
    * Extracts duration data and transitions partials from the loaded data. There are 3 types of partials: color, mask
     * and push. Each one can be used as either and in our out transition.
    */
    get transitionsPlugin() {
        if (!this.#plugins[TRANSITIONS_PLUGIN]) {
            this.useTransitionsPlugin();
        }
        return this.#plugins[TRANSITIONS_PLUGIN];
    }

    isOriginalValueEqualTo(json: Lottie) {
        return isEqual(this.#originalData, json);
    }

    isCurrentValueEqualTo(json: Lottie) {
        return this.#currentData === json;
    }

    getFrameAtTime(timeInSeconds: number, playbackMode: string) {
        const lottieDurationInSeconds = this.duration / 1000;
        let lottieTimeInSeconds;
        switch (playbackMode) {
            case Config.PLAYBACK_LOOP:
                lottieTimeInSeconds = timeInSeconds % lottieDurationInSeconds;
                break;
            case Config.PLAYBACK_HOLD:
                lottieTimeInSeconds = Math.min(timeInSeconds, lottieDurationInSeconds);
                break;
            default:
                throw new Error(`Unrecognized playbackMode ${playbackMode}`);
        }
        const frame = Math.floor(lottieTimeInSeconds * this.frameRate);
        return frame > this.lastFrameIndex ? this.lastFrameIndex : frame;
    }

    #loadColorsPlugin() {
        this.#plugins[COLORS_PLUGIN] = new ColorsPlugin();
    }

    #updateColorsPlugin() {
        this.colorsPlugin.update(this.#currentData);
    }

    useColorsPlugin() {
        this.#loadColorsPlugin();
        this.#updateColorsPlugin();
    }

    replaceColor(colorMap: ColorMap) {
        if (!this.colorsPlugin.shouldReplaceColor(colorMap)) {
            return;
        }
        this.#updateState((state) => {
            this.colorsPlugin.replaceColor(state, colorMap);
        });
    }

    replaceColors(colorMaps: ColorMap[]) {
        colorMaps.forEach((colorMap) => this.replaceColor(colorMap));
    }

    #loadTransitionsPlugin() {
        this.#plugins[TRANSITIONS_PLUGIN] = new TransitionsPlugin();
    }

    #updateTransitionsPlugin() {
        this.transitionsPlugin.update(this.#currentData);
    }

    useTransitionsPlugin() {
        this.#loadTransitionsPlugin();
        this.#updateTransitionsPlugin();
    }

    updatePlaybackSpeed(value: number) {
        const dependentPlugins = [TRANSITIONS_PLUGIN];
        if (value === 0) {
            if (this.#currentData.op === 0) {
                return;
            }
            this.#updateState((state) => {
                state.op = 0;
            }, dependentPlugins);
        }
        else {
            if (this.#currentData.fr === value) {
                return;
            }
            const updatedFrameRate = this.originalFrameRate * value;
            this.#updateState((state) => {
                state.op = this.originalValue.op; // reset to original value (in case it was set to 0 before)
                state.fr = updatedFrameRate;
            }, dependentPlugins);
        }
    }

    updateDuration(value: number) {
        const updatedPlaybackSpeed = value ? this.originalDuration / value : 0;
        this.updatePlaybackSpeed(updatedPlaybackSpeed);
    }

    #updatePlugins(pluginNames: string[] = []) {
        // update only if the plugin is loaded
        pluginNames.forEach(name => {
            const plugin: BasePlugin = this.#plugins[name];
            plugin && plugin.update(this.#currentData);
        });
    }

    #updateState(changeFn: ChangeFn, dependentPluginNames: string[] = []) {
        this.#currentData = { ...this.#currentData };
        changeFn(this.#currentData);
        this.#updatePlugins(dependentPluginNames);
    }
}
